import { PublicationSetting } from 'data/generated/graphql';
import { AllessehContentQueryBody } from 'hooks/useAllessehContentQuery';
import { getLayoutId } from 'utils/layoutUtils';
import { QUERY_CONFIG_BARRONS, QueryOptionBarrons } from './query-rule-configs/barrons';
import { QUERY_CONFIG_CWSJ, QueryOptionCwsj } from './query-rule-configs/cwsj';
import { DEFAULT_QUERY } from './query-rule-configs/default';
import { QUERY_CONFIG_JWSJ, QueryOptionJwsj } from './query-rule-configs/jwsj';
import { getWSJDefaultQuery, QUERY_CONFIG_WSJ, QueryOptionWsj } from './query-rule-configs/wsj';
import { QUERY_CONFIG_WSJOPINION, QueryOptionWsjopinion } from './query-rule-configs/wsjopinion';

export type QueryOption =
  QueryOptionWsj | QueryOptionJwsj | QueryOptionWsjopinion | QueryOptionCwsj | QueryOptionBarrons;
type QueryOptionsMap = Record<string, QueryOption[]>;

type BaseQueryConfig = {
  default?: QueryOptionsMap;
  homepage?: QueryOptionsMap;
  [layoutId: string]: QueryOptionsMap | undefined;
};

const QUERY_CONFIG: Record<string, BaseQueryConfig> = {
  wsj: QUERY_CONFIG_WSJ,
  jwsj: QUERY_CONFIG_JWSJ,
  cwsj: QUERY_CONFIG_CWSJ,
  wsjopinion: QUERY_CONFIG_WSJOPINION,
  barrons: QUERY_CONFIG_BARRONS
} as const;

export type QueryConfigKey = keyof typeof QUERY_CONFIG;
export type PublicationKey = string;

export const getDefaultQuery = ({
  publicationKey,
  allessehId = '',
  treatmentType = '',
  publicationSetting
}: {
  publicationKey: PublicationKey;
  allessehId?: string;
  treatmentType?: string;
  publicationSetting: PublicationSetting;
}): QueryOption => {
  if (publicationKey === 'wsj') {
    return getWSJDefaultQuery(allessehId, treatmentType);
  }

  const config = QUERY_CONFIG[publicationKey] as (typeof QUERY_CONFIG)[QueryConfigKey] | undefined;

  return (
    config?.default?.default?.[0] ??
    ({
      query: {
        ...DEFAULT_QUERY,
        ...(JSON.parse(publicationSetting.baseAllessehQuery) as AllessehContentQueryBody)
      },
      label: 'Custom Query',
      type: null,
      url: null
    } as unknown as QueryOption)
  );
};

export const getQueryOptions = ({
  publicationKey,
  allessehId,
  treatmentType,
  publicationSetting
}: {
  publicationKey: PublicationKey;
  allessehId: string;
  treatmentType: string;
  publicationSetting: PublicationSetting;
}): QueryOption[] => {
  const config = QUERY_CONFIG[publicationKey] as (typeof QUERY_CONFIG)[QueryConfigKey] | undefined;
  if (!config) {
    return [getDefaultQuery({ publicationKey, allessehId, treatmentType, publicationSetting })];
  }

  const layoutId = getLayoutId(allessehId);

  // layoutId starts with 'homepage' for IDs like UCSD_WSJ_HOMEPAGE_TEST-COLLECTION where pageType
  // isn't directly accessible here — both empty and homepage-prefixed layoutIds map to homepage config
  if ((!layoutId || layoutId.startsWith('homepage')) && config.homepage?.[treatmentType]) {
    return config.homepage[treatmentType];
  }

  if (!config[layoutId]?.[treatmentType]) {
    return [getDefaultQuery({ publicationKey, allessehId, treatmentType, publicationSetting })];
  }

  return (
    config[layoutId]?.[treatmentType] ?? [
      getDefaultQuery({ publicationKey, allessehId, treatmentType, publicationSetting })
    ]
  );
};
